/* @flow */
import { cloneDeep } from 'lodash';
import Entity from 'lib/Entity';
import Activity from 'entities/Activity';
import RelatedUserSummary from 'entities/RelatedUserSummary';
import apiRequest from 'lib/apiRequest';

export const TYPE_WITHDRAWAL = 'withdrawal';
export const TYPE_PAYMENT_SENT = 'payment_sent';
export const TYPE_PAYMENT_RECEIVED = 'payment_received';
export const TYPE_REFUND_SENT = 'refund_sent';

export const STATUS_PENDING = 'pending';

class Transaction extends Entity {
  initialize() {
    this.idAttribute = 'uuid';
  }

  defaults() {
    return {
      amount_per_person: 0,
      application_fee: 2,
      charge_id: '',
      payment_made_for: '',
      payment_req_note: '',
      transfer_id: '',
      type: TYPE_PAYMENT_SENT,
    };
  }

  parse(response: Object) {
    const transaction = cloneDeep(response);

    if ('created_at' in transaction) {
      transaction['created_at'] = new Date(transaction['created_at']);
    }

    if ('activity' in transaction) {
      transaction['activity'] = new Activity(transaction['activity']);
    }

    if ('paid_to' in transaction) {
      transaction['paid_to'] = new RelatedUserSummary(transaction['paid_to']);
    }

    if ('payer' in transaction) {
      transaction['payer'] = new RelatedUserSummary(transaction['payer']);
    }

    if ('available_at' in transaction) {
      transaction['available_at'] = new Date(transaction['available_at']);
    }

    return transaction;
  }

  // Indicates if the transaction is made by a user with no Remind account
  isByAnonymousUser() {
    return !(this.get('user_uuid') || this.get('unverified_user_uuid'));
  }

  isWithdrawal() {
    return this.getType() === TYPE_WITHDRAWAL;
  }

  isPaymentSent() {
    return this.getType() === TYPE_PAYMENT_SENT;
  }

  isRefundSent() {
    return this.getType() === TYPE_REFUND_SENT;
  }

  isRefundable(): boolean {
    return !!this.get('refundable');
  }

  isPaymentReceived() {
    return this.getType() === TYPE_PAYMENT_RECEIVED;
  }

  isPending() {
    return this.getStatus() === STATUS_PENDING;
  }

  getStatus() {
    return this.get('status');
  }

  getUuid() {
    return this.get('uuid');
  }

  getAmount() {
    return this.get('amount');
  }

  getRefundedTransactionUUID(): ?string {
    return this.get('refunded_transaction_uuid');
  }

  getApplicationFee() {
    return this.get('application_fee');
  }

  getChargeId() {
    return this.get('charge_id');
  }

  getCreatedAt() {
    return this.get('created_at');
  }

  getAddress() {
    return this.get('address');
  }

  getName() {
    return this.get('name');
  }

  getPaidToUserUuid() {
    return this.get('paid_to_user_uuid');
  }

  getPayer() {
    return this.get('payer');
  }

  getPaymentMadeFor() {
    return this.get('payment_made_for');
  }

  getPaymentReqNote() {
    return this.get('payment_req_note');
  }

  getActivity() {
    return this.get('activity');
  }

  getPaidTo() {
    return this.get('paid_to');
  }

  getTransferId() {
    return this.get('transfer_id');
  }

  getType() {
    return this.get('type');
  }

  getUserUuid() {
    return this.get('user_uuid');
  }

  getUnverifiedUserUuid() {
    return this.get('unverified_user_uuid');
  }

  getAvailableAt() {
    return this.get('available_at');
  }

  // $FlowFixMe
  claim(args) {
    return apiRequest(`/payments/transactions/${this.getUuid()}/claim`, 'POST', args)
      .then(transaction => Transaction.fromJSON(transaction));
  }

  static createWithdrawTransaction(activityId, destination, amount) {
    return apiRequest(`/activities/${activityId}/withdraw`, 'POST', {
      destination,
      amount,
    }).then(transaction => Transaction.fromJSON(transaction));
  }

  static createPayment(activityUUID, paymentMadeFor, amount, token, address, name, recipientUuid) {
    const card = token.startsWith('card_') ? { card_id: token } : { token };

    return apiRequest(`/activities/${activityUUID}/charges`, 'POST', {
      // $FlowFixMe
      charge: Object.assign(card, {
        payment_req_uuid: activityUUID,
        payment_made_for: paymentMadeFor,
        recipient_uuid: recipientUuid,
        amount,
        address,
        name,
      }),
    }).then(transaction => Transaction.fromJSON(transaction));
  }
}

export default Transaction;



// WEBPACK FOOTER //
// ./src/entities/Transaction.js